import React, { useEffect, useRef, useMemo } from 'react';

interface SpaceBackgroundProps {
  theme?: 'light' | 'dark' | 'mixed';
  intensity?: 'none' | 'minimal' | 'normal' | 'cosmic';
  className?: string;
}

// Generate star positions for consistent animation
const generateStars = (count: number, size: 'small' | 'medium' | 'large') => {
  return Array.from({ length: count }, (_, i) => {
    // Determine star placement zones to avoid central content
    const zone = Math.random();
    let x, y;
    
    if (zone < 0.35) {
      // Left side (0-22% of screen width)
      x = Math.random() * 22;
      y = Math.random() * 100;
    } else if (zone < 0.7) {
      // Right side (78-100% of screen width)
      x = 78 + Math.random() * 22;
      y = Math.random() * 100;
    } else if (zone < 0.85) {
      // Top edge (full width but top 20% of screen height)
      x = Math.random() * 100;
      y = Math.random() * 20;
    } else if (zone < 0.95) {
      // Bottom edge (full width but bottom 20% of screen height)
      x = Math.random() * 100;
      y = 80 + Math.random() * 20;
    } else {
      // Few stars in center area for some depth (22-78% width, 20-80% height)
      x = 22 + Math.random() * 56;
      y = 20 + Math.random() * 60;
    }
    
    return {
      id: i,
      x,
      y,
      delay: Math.random() * 2,
      duration: 2 + Math.random() * 3,
      size: size === 'small' ? 1 : size === 'medium' ? 2 : 3,
    };
  });
};

// Generate falling stars with different speeds and directions
const generateFallingStars = (count: number, layer: 1 | 2 | 3) => {
  return Array.from({ length: count }, (_, i) => {
    // Determine star placement zones to avoid central content
    const zone = Math.random();
    let x, y;
    
    if (zone < 0.4) {
      // Left side (0-20% of screen width)
      x = Math.random() * 20;
      y = Math.random() * 100;
    } else if (zone < 0.8) {
      // Right side (80-100% of screen width)
      x = 80 + Math.random() * 20;
      y = Math.random() * 100;
    } else if (zone < 0.9) {
      // Top edge (full width but top 15% of screen height)
      x = Math.random() * 100;
      y = Math.random() * 15;
    } else {
      // Bottom edge (full width but bottom 15% of screen height)
      x = Math.random() * 100;
      y = 85 + Math.random() * 15;
    }
    
    return {
      id: i,
      x,
      y,
      delay: Math.random() * 10,
      duration: layer === 1 ? 15 + Math.random() * 10 : layer === 2 ? 20 + Math.random() * 15 : 30 + Math.random() * 20,
      size: layer === 1 ? 1 : layer === 2 ? 1.5 : 2,
      opacity: layer === 1 ? 0.8 : layer === 2 ? 0.6 : 0.4,
    };
  });
};

// Generate shooting stars
const generateShootingStars = (count: number) => {
  return Array.from({ length: count }, (_, i) => {
    // Shooting stars mostly from the edges
    const zone = Math.random();
    let x, y;
    
    if (zone < 0.5) {
      // From left edge
      x = Math.random() * 15;
      y = Math.random() * 50;
    } else if (zone < 0.8) {
      // From right edge
      x = 85 + Math.random() * 15;
      y = Math.random() * 50;
    } else {
      // From top edge
      x = Math.random() * 100;
      y = Math.random() * 10;
    }
    
    return {
      id: i,
      x,
      y,
      delay: Math.random() * 15,
      duration: 2 + Math.random() * 3,
    };
  });
};

export const SpaceBackground: React.FC<SpaceBackgroundProps> = ({
  theme = 'light',
  intensity = 'normal',
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Generate star configurations based on intensity
  const starConfig = useMemo(() => {
    const configs = {
      none: { small: 0, medium: 0, large: 0, falling: { layer1: 0, layer2: 0, layer3: 0 }, shooting: 0 },
      minimal: { small: 30, medium: 15, large: 5, falling: { layer1: 20, layer2: 15, layer3: 10 }, shooting: 1 },
      normal: { small: 80, medium: 40, large: 15, falling: { layer1: 40, layer2: 30, layer3: 20 }, shooting: 2 },
      cosmic: { small: 150, medium: 75, large: 25, falling: { layer1: 80, layer2: 60, layer3: 40 }, shooting: 3 },
    };
    return configs[intensity];
  }, [intensity]);

  const smallStars = useMemo(() => generateStars(starConfig.small, 'small'), [starConfig.small]);
  const mediumStars = useMemo(() => generateStars(starConfig.medium, 'medium'), [starConfig.medium]);
  const largeStars = useMemo(() => generateStars(starConfig.large, 'large'), [starConfig.large]);
  
  // Falling stars layers
  const fallingStarsLayer1 = useMemo(() => generateFallingStars(starConfig.falling.layer1, 1), [starConfig.falling.layer1]);
  const fallingStarsLayer2 = useMemo(() => generateFallingStars(starConfig.falling.layer2, 2), [starConfig.falling.layer2]);
  const fallingStarsLayer3 = useMemo(() => generateFallingStars(starConfig.falling.layer3, 3), [starConfig.falling.layer3]);
  
  // Shooting stars
  const shootingStars = useMemo(() => generateShootingStars(starConfig.shooting), [starConfig.shooting]);

  const isDark = theme === 'dark' || theme === 'mixed';

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 overflow-hidden pointer-events-none z-0 ${className}`}
      style={{
        background: isDark 
          ? 'linear-gradient(135deg, #1A1625 0%, #2A203C 100%)'
          : 'linear-gradient(135deg, #FAFAFA 0%, #F0F4F8 100%)'
      }}
    >
      {/* Nebula Clouds Background */}
      <div 
        className="absolute inset-0 opacity-60"
        style={{
          background: isDark
            ? `radial-gradient(circle at 20% 80%, rgba(42, 32, 60, 0.4) 0%, transparent 50%),
               radial-gradient(circle at 80% 20%, rgba(123, 93, 153, 0.25) 0%, transparent 50%),
               radial-gradient(circle at 60% 40%, rgba(99, 102, 241, 0.15) 0%, transparent 40%)`
            : `radial-gradient(circle at 20% 80%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
               radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.08) 0%, transparent 50%),
               radial-gradient(circle at 60% 40%, rgba(6, 182, 212, 0.05) 0%, transparent 40%)`
        }}
      />

      {/* Falling Stars Layer 1 - Fastest */}
      <div className="absolute inset-0">
        {fallingStarsLayer1.map((star) => (
          <div
            key={`falling-1-${star.id}`}
            className="absolute rounded-full animate-parallax-fast"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: isDark ? '#F76700' : '#F76700',
              opacity: star.opacity,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
              boxShadow: '0 0 4px rgba(247, 103, 0, 0.6)',
            }}
          />
        ))}
      </div>

      {/* Falling Stars Layer 2 - Medium Speed */}
      <div className="absolute inset-0">
        {fallingStarsLayer2.map((star) => (
          <div
            key={`falling-2-${star.id}`}
            className="absolute rounded-full animate-parallax-medium"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: isDark ? '#7B5D99' : '#6366f1',
              opacity: star.opacity,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
              boxShadow: isDark ? '0 0 4px rgba(123, 93, 153, 0.6)' : '0 0 4px rgba(99, 102, 241, 0.4)',
            }}
          />
        ))}
      </div>

      {/* Falling Stars Layer 3 - Slowest */}
      <div className="absolute inset-0">
        {fallingStarsLayer3.map((star) => (
          <div
            key={`falling-3-${star.id}`}
            className="absolute rounded-full animate-parallax-slow"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: isDark ? '#EFEAE3' : '#ec4899',
              opacity: star.opacity,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
              boxShadow: isDark ? '0 0 6px rgba(239, 234, 227, 0.8)' : '0 0 6px rgba(236, 72, 153, 0.6)',
            }}
          />
        ))}
      </div>

      {/* Twinkling Static Stars Layer */}
      <div className="absolute inset-0">
        {smallStars.map((star) => (
          <div
            key={`small-${star.id}`}
            className="absolute rounded-full animate-twinkle"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: isDark ? '#EFEAE3' : '#F76700',
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Medium Stars Layer */}
      <div className="absolute inset-0">
        {mediumStars.map((star) => (
          <div
            key={`medium-${star.id}`}
            className="absolute rounded-full animate-twinkle"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: isDark ? '#7B5D99' : '#6366f1',
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
              boxShadow: isDark ? '0 0 4px rgba(123, 93, 153, 0.6)' : '0 0 4px rgba(99, 102, 241, 0.4)',
            }}
          />
        ))}
      </div>

      {/* Large Stars Layer */}
      <div className="absolute inset-0">
        {largeStars.map((star) => (
          <div
            key={`large-${star.id}`}
            className="absolute rounded-full animate-twinkle"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: isDark ? '#a855f7' : '#ec4899',
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
              boxShadow: isDark ? '0 0 6px rgba(168, 85, 247, 0.8)' : '0 0 6px rgba(236, 72, 153, 0.6)',
            }}
          />
        ))}
      </div>

      {/* Shooting Stars */}
      <div className="absolute inset-0">
        {shootingStars.map((star) => (
          <div
            key={`shooting-${star.id}`}
            className="absolute animate-shooting-star"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
            }}
          >
            {/* Simple glowing circle */}
            <div 
              className="w-1.5 h-1.5 bg-vibrant-orange rounded-full"
              style={{
                boxShadow: '0 0 8px rgba(247, 103, 0, 0.8), 0 0 16px rgba(247, 103, 0, 0.4), 0 0 24px rgba(247, 103, 0, 0.2)',
              }}
            />
          </div>
        ))}
      </div>

      {/* Distant Planet - Only visible on normal/cosmic intensity */}
      {intensity !== 'minimal' && intensity !== 'none' && (
        <div 
          className="absolute w-32 h-32 rounded-full opacity-30 animate-satellite-orbit"
          style={{
            top: '10%',
            right: '5%',
            background: isDark 
              ? 'radial-gradient(circle at 30% 30%, rgba(123, 93, 153, 0.4) 0%, rgba(42, 32, 60, 0.6) 100%)'
              : 'radial-gradient(circle at 30% 30%, rgba(99, 102, 241, 0.4) 0%, rgba(168, 85, 247, 0.2) 100%)',
            filter: 'blur(1px)',
          }}
        />
      )}

      {/* Floating Particles - Cosmic intensity only */}
      {intensity === 'cosmic' && (
        <div className="absolute inset-0">
          {Array.from({ length: 20 }, (_, i) => {
            // Position particles on sides and edges, avoiding central content
            const zone = Math.random();
            let left, top;
            
            if (zone < 0.4) {
              // Left side (0-20% of screen width)
              left = Math.random() * 20;
              top = Math.random() * 100;
            } else if (zone < 0.8) {
              // Right side (80-100% of screen width)
              left = 80 + Math.random() * 20;
              top = Math.random() * 100;
            } else {
              // Top/bottom edges (full width but edges)
              left = Math.random() * 100;
              top = Math.random() < 0.5 ? Math.random() * 20 : 80 + Math.random() * 20;
            }
            
            return (
              <div
                key={`particle-${i}`}
                className="absolute w-0.5 h-0.5 bg-cosmic-teal rounded-full opacity-60 animate-rocket-float"
                style={{
                  left: `${left}%`,
                  top: `${top}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${3 + Math.random() * 4}s`,
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SpaceBackground; 